import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

const MOBILE_GALLERY_QUERY = '(max-width: 749px)';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.#getZoomDialog()?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupMobileGallery();
  }

  #controller = new AbortController();
  #galleryScroller;
  #galleryScrollbar;
  #galleryScrollbarThumb;
  #galleryResizeObserver;
  #productDetails;
  #updateFrame;
  #mobileQuery;
  #isRtl = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#galleryResizeObserver?.disconnect();
    window.cancelAnimationFrame(this.#updateFrame);
  }

  #setupMobileGallery() {
    const scroller = this.querySelector('slideshow-slides');
    const scrollbar = this.querySelector('.cp-gallery-scrollbar');
    const thumb = scrollbar?.querySelector('.cp-gallery-scrollbar__thumb');

    if (!scroller || !scrollbar || !thumb) return;

    this.#galleryScroller = scroller;
    this.#galleryScrollbar = scrollbar;
    this.#galleryScrollbarThumb = thumb;
    this.#productDetails = this.closest('.product-information')?.querySelector('.product-details');

    const { signal } = this.#controller;

    // Créés une fois : `matchMedia` et `getComputedStyle` sont trop chers pour
    // être rappelés à chaque événement de défilement.
    this.#mobileQuery = window.matchMedia(MOBILE_GALLERY_QUERY);
    this.#isRtl = getComputedStyle(scroller).direction === 'rtl';

    // Aucun gestionnaire tactile ici : la navigation entre images est laissée au
    // navigateur. Le conteneur porte `scroll-snap-type: x mandatory` et chaque
    // diapo `scroll-snap-stop: always` (cp-product.css), ce qui suffit à
    // enchaîner les images. Toute animation programmée sur ce scroller entrait
    // en course avec l'inertie native et produisait un dépassement suivi d'un
    // retour, mesuré jusqu'à 45 px en arrière.
    scroller.addEventListener('scroll', this.#requestGalleryUpdate, { passive: true, signal });
    window.addEventListener('scroll', this.#requestGalleryUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#handleResize, { passive: true, signal });

    this.#galleryResizeObserver = new ResizeObserver(this.#requestGalleryUpdate);
    this.#galleryResizeObserver.observe(scroller);

    requestAnimationFrame(this.#updateMobileGallery);
  }

  /**
   * Le défilement émet bien plus d'événements qu'il n'y a d'images à l'écran, et
   * chaque passage lit la géométrie puis écrit un attribut : enchaînés, ils
   * forçaient un recalcul de mise en page par événement. Mesuré à 117 ms dans
   * une seule frame sur un processeur bridé six fois. Un seul passage par frame
   * suffit à tenir l'affichage à jour.
   */
  #requestGalleryUpdate = () => {
    if (this.#updateFrame) return;

    this.#updateFrame = requestAnimationFrame(() => {
      this.#updateFrame = undefined;
      this.#updateMobileGallery();
    });
  };

  #handleResize = () => {
    const scroller = this.#galleryScroller;
    if (scroller) this.#isRtl = getComputedStyle(scroller).direction === 'rtl';

    this.#requestGalleryUpdate();
  };

  #updateMobileGallery = () => {
    this.#updateSwipeAvailability();

    const scroller = this.#galleryScroller;
    const scrollbar = this.#galleryScrollbar;
    const thumb = this.#galleryScrollbarThumb;

    if (!scroller || !scrollbar || !thumb) return;

    const isMobile = this.#mobileQuery?.matches ?? false;
    const scrollableWidth = scroller.scrollWidth - scroller.clientWidth;
    const isScrollable = isMobile && scrollableWidth > 1;

    scrollbar.hidden = !isScrollable;
    if (!isScrollable) return;

    const ratio = scroller.clientWidth / scroller.scrollWidth;
    const maxTravel = scroller.clientWidth * (1 - ratio);
    const progress = Math.min(1, Math.max(0, Math.abs(scroller.scrollLeft) / scrollableWidth));
    const position = (this.#isRtl ? 1 - progress : progress) * maxTravel;

    // Écrire une valeur identique invalide quand même le style : on ne touche au
    // curseur que lorsqu'il bouge vraiment.
    const width = `${ratio * 100}%`;
    const transform = `translateX(${position}px)`;

    if (thumb.style.width !== width) thumb.style.width = width;
    if (thumb.style.transform !== transform) thumb.style.transform = transform;
  };

  #updateSwipeAvailability = () => {
    const scroller = this.#galleryScroller;

    if (!scroller || !(this.#mobileQuery?.matches ?? false)) {
      this.removeAttribute('data-mobile-swipe-ready');
      return;
    }

    const rect = scroller.getBoundingClientRect();
    const detailsTop = this.#productDetails?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
    const isFullyVisible =
      rect.height > 0 &&
      rect.top >= -1 &&
      rect.bottom <= window.innerHeight + 1 &&
      detailsTop >= rect.bottom - 1;

    this.toggleAttribute('data-mobile-swipe-ready', isFullyVisible);
  };


  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * @returns {import('./zoom-dialog').ZoomDialog | null}
   */
  #getZoomDialog() {
    const { zoomDialogComponent } = this.refs;

    if (zoomDialogComponent && typeof zoomDialogComponent.open === 'function') {
      return zoomDialogComponent;
    }

    const zoomDialog = this.querySelector('zoom-dialog');

    if (zoomDialog && typeof zoomDialog.open === 'function') {
      return /** @type {import('./zoom-dialog').ZoomDialog} */ (zoomDialog);
    }

    return null;
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.#getZoomDialog()?.open(index, event);
  }

  /**
   * Preloads an image : prépare la visionneuse à sa taille d'affichage. Charger
   * ici l'original (3840) le faisait décoder à l'ouverture, qui se figeait.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    this.#getZoomDialog()?.preloadMedia(index);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
